/* eslint-disable react/prop-types -- the codebase does not use PropTypes (not a dependency) */
import { useState } from 'react';
import { Button, message } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import API from '../API/API';
import {
  GOOGLE_ADS_LOOKBACK_WARNING,
  buildGoogleAdsFetchParams,
  buildGoogleAdsRows,
  buildGoogleAdsWorkbook,
  extractLeadsFromResponse,
  getGoogleAdsFileName,
  isBeyondGoogleAdsLookback,
  writeGoogleAdsWorkbook
} from './googleAdsSheetUtils';

// Super admin only, same as the Export button in MyLeads.jsx (backend also blocks it).
import { isSuperAdminUser } from '../utils/roles';

// "Download Google Ads Sheet" button for the Export Leads popup. Admins only; it uses
// the popup's date range and writes every time in US Pacific time.
const GoogleAdsSheetDownload = ({ dateRange, disabled = false }) => {
  const [generating, setGenerating] = useState(false);

  if (!isSuperAdminUser()) {
    return null;
  }

  const [startDate, endDate] = Array.isArray(dateRange) ? dateRange : [];
  const hasRange = Boolean(startDate && endDate);

  const handleDownload = async () => {
    if (!hasRange || generating) return;

    setGenerating(true);
    try {
      if (isBeyondGoogleAdsLookback(startDate)) {
        message.warning(GOOGLE_ADS_LOOKBACK_WARNING, 8);
      }

      const response = await API.leadGetAll(buildGoogleAdsFetchParams(startDate, endDate));
      const leads = extractLeadsFromResponse(response);

      if (!leads) {
        message.error(response?.message || 'Failed to fetch leads for the Google Ads sheet');
        return;
      }

      if (leads.length === 0) {
        message.warning('No leads found for the selected date range');
        return;
      }

      const rows = buildGoogleAdsRows(leads);

      if (rows.length === 0) {
        message.info(
          `No Sale Made, Callback or Follow-up leads in the selected date range (${leads.length} leads fetched). Nothing was downloaded.`,
          6
        );
        return;
      }

      writeGoogleAdsWorkbook(buildGoogleAdsWorkbook(rows), getGoogleAdsFileName(startDate, endDate));
      message.success(
        `Google Ads sheet downloaded: ${rows.length} rows exported from ${leads.length} leads fetched.`,
        6
      );
    } catch (error) {
      console.error('Error generating Google Ads sheet:', error);
      message.error('Failed to generate the Google Ads sheet');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      loading={generating}
      disabled={disabled || !hasRange}
      icon={<GoogleOutlined />}
      size="large"
      title="Google Click ID, Email, Phone Number and Conversion Time (US Pacific) for Sale Made, Callback and Follow-up leads"
      style={{
        flex: 1,
        height: 48,
        fontSize: 16,
        fontWeight: 600,
        borderColor: '#34a853',
        color: '#188038'
      }}
    >
      Download Google Ads Sheet
    </Button>
  );
};

export default GoogleAdsSheetDownload;
